// Deep links from outside the app (report subscription emails, mainly).
//
// A signed-out click goes through /login, which used to drop the target and
// land on /dashboard. ProtectedRoute remembers where the user was headed;
// sign-in resumes there. localStorage rather than sessionStorage because the
// magic-link sign-in can finish in a different tab from the one clicked.
//
// `?org=<id>` names the organization the link belongs to, so the app opens in
// that org instead of whichever one the user last had selected. It applies
// once: clearRequestedOrg() drops it after load, or a reload would keep
// pulling the user back into the linked org after they switch away.

const KEY = 'lodgeic.deepLink';
const TTL_MS = 30 * 60 * 1000;

// The URL parser reads "/\host" and "/\t/host" as "//host", so compare origins
// rather than prefixes; React Router throws on a cross-origin navigate.
export const isSameOriginPath = (to) => {
  try {
    return new URL(to, window.location.origin).origin === window.location.origin;
  } catch (_) {
    return false;
  }
};

const isSafePath = (to) => typeof to === 'string' && to.startsWith('/') && !to.startsWith('/login') && isSameOriginPath(to);

export function rememberDeepLink(to) {
  if (!isSafePath(to) || to === '/' || to.startsWith('/dashboard')) return;
  try {
    localStorage.setItem(KEY, JSON.stringify({ to, at: Date.now() }));
  } catch (_) { /* storage blocked — the user just lands on the default page */ }
}

function readDeepLink() {
  try {
    const saved = JSON.parse(localStorage.getItem(KEY) || 'null');
    if (!saved || !isSafePath(saved.to) || Date.now() - saved.at > TTL_MS) return null;
    return saved.to;
  } catch (_) {
    return null;
  }
}

/** Where to go after sign-in: the remembered deep link, else `fallback`. */
export function consumeDeepLink(fallback = '/dashboard') {
  const to = readDeepLink();
  try { localStorage.removeItem(KEY); } catch (_) { /* ignore */ }
  return to || fallback;
}

/** Organization id a deep link asks for — the current URL's, else the pending link's. */
export function requestedOrgId() {
  const fromSearch = (search) => {
    try { return new URLSearchParams(search).get('org'); } catch (_) { return null; }
  };
  const current = typeof window !== 'undefined' ? fromSearch(window.location.search) : null;
  if (current) return current;
  const pending = readDeepLink();
  const q = pending ? pending.indexOf('?') : -1;
  return q >= 0 ? fromSearch(pending.slice(q)) : null;
}

const stripOrg = (to) => {
  const url = new URL(to, 'http://lodgeic.local');
  if (!url.searchParams.has('org')) return null;
  url.searchParams.delete('org');
  return `${url.pathname}${url.search}${url.hash}`;
};

/**
 * The requested org has been applied (or refused): drop `?org=` from the
 * address bar and from the pending link, keeping every other param and the hash.
 */
export function clearRequestedOrg() {
  if (typeof window !== 'undefined') {
    try {
      const { pathname, search, hash } = window.location;
      const stripped = stripOrg(`${pathname}${search}${hash}`);
      if (stripped) {
        window.history.replaceState(window.history.state, '', stripped);
        // BrowserRouter only re-reads the URL on popstate; without it a later
        // setSearchParams would write the stale ?org= straight back.
        window.dispatchEvent(new PopStateEvent('popstate', { state: window.history.state }));
      }
    } catch (_) { /* leave the URL as it is */ }
  }
  try {
    const saved = JSON.parse(localStorage.getItem(KEY) || 'null');
    const stripped = saved && isSafePath(saved.to) ? stripOrg(saved.to) : null;
    if (stripped) localStorage.setItem(KEY, JSON.stringify({ ...saved, to: stripped }));
  } catch (_) { /* storage blocked — nothing pending to rewrite */ }
}
